// src/services/arService.ts
// appeear.me AR Service wrapper for ORIFOLI
// Simplified version - AR models are created on appeear.me, then linked to ORIFOLI characters

import { config } from '../config';
import { auth } from './firebase';

// API Response types
export interface ARModelInfo {
  modelId: string;
  name: string;
  status: string;
  glbUrl?: string;
  usdzUrl?: string;
  thumbnailUrl?: string;
  displayType: 'flat' | 'acrylic_stand';
  viewCount: number;
  createdAt: string;
  expiresAt?: string;
}

// Helper to get auth token
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

export const arService = {
  /**
   * Get user's AR models from appeear.me
   * This fetches all AR models created by the current user
   */
  async getMyARModels(): Promise<ARModelInfo[]> {
    const token = await getAuthToken();

    const response = await fetch(
      `${config.apiBaseUrl}/api/ar/models`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch AR models: ${errorText}`);
    }

    const data = await response.json();
    // Map the response format from ar.py list_user_models
    return (data.models || []).map((model: any) => ({
      modelId: model.id || model.model_id,
      name: model.name || 'Untitled',
      status: model.status || 'active',
      glbUrl: model.glbUrl || model.glb_url,
      usdzUrl: model.usdzUrl || model.usdz_url,
      thumbnailUrl: model.thumbnailPath || model.thumbnail_url,
      displayType: model.displayType || model.display_type || 'acrylic_stand',
      viewCount: model.viewCount || model.view_count || 0,
      createdAt: model.createdAt || model.created_at || '',
      expiresAt: model.expiresAt || model.expires_at,
    }));
  },

  /**
   * Get AR model info for viewing (public endpoint)
   */
  async getARModelInfo(modelId: string): Promise<ARModelInfo> {
    const response = await fetch(
      `${config.apiBaseUrl}/api/ar/view/${modelId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get AR model: ${errorText}`);
    }

    const data = await response.json();
    return {
      modelId: data.model_id,
      name: data.name,
      status: 'active',
      glbUrl: data.glb_url,
      usdzUrl: data.usdz_url,
      thumbnailUrl: data.thumbnail_url,
      displayType: data.display_type,
      viewCount: data.view_count || 0,
      createdAt: data.created_at,
    };
  },

  /**
   * Unlink AR model from character
   */
  async unlinkARModel(characterId: string): Promise<void> {
    const token = await getAuthToken();

    const response = await fetch(
      `${config.apiBaseUrl}/api/orifoli/characters/${characterId}/ar`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to unlink AR model: ${errorText}`);
    }
  },

  /**
   * Get AR viewer URL
   */
  getViewerUrl(modelId: string): string {
    return `${config.appeearMeUrl}/${modelId}`;
  },
};
